// SpendCard.tsx — amount spent on a pushed sync, cost per lead, campaign split
// and change history (ADMIN ONLY — render it only when viewer.can_manage_spend).

import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, History, IndianRupee, Pencil, Plus } from 'lucide-react';
import { G, C } from '../../../components/GlobalComponents/uiTokens';
import SpendEditModal from './SpendEditModal';
import { SpendChange, SpendForSync, SyncSpend, campaignLabel, fmtINR, inclExcl, spendApi, spendError } from './spendTypes';
import { fmtDateTime, pillStyle, StatTile, TONES, userLabel } from '../../SheetsManager/parts/SheetImportDetailsModal';

interface SpendCardProps {
  importId: string;
  sheetName?: string;
  /** Bump to refetch (e.g. right after a push). */
  refreshKey?: number;
  onChanged?: (s: SyncSpend) => void;
}

const SpendCard: React.FC<SpendCardProps> = ({ importId, sheetName, refreshKey, onChanged }) => {
  const [info, setInfo] = useState<SpendForSync | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const r = await spendApi.get(importId);
      setInfo({ data: r.data, campaigns: r.campaigns ?? [], leads_count: r.leads_count ?? 0 });
    } catch (e: any) {
      setError(spendError(e, 'Could not load the amount.'));
    } finally {
      setLoading(false);
    }
  }, [importId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const s = info?.data ?? null;

  const wrap = (children: React.ReactNode) => (
    <div style={{ border: `1px solid ${G.line}`, borderRadius: '9.2px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
          <span
            style={{
              width: 24,
              height: 24,
              borderRadius: '7px',
              background: C.limeDim,
              border: `1px solid ${C.limeBorder}`,
              color: '#4a5e00',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IndianRupee size={13} />
          </span>
          <span style={{ fontSize: '0.76rem', fontWeight: 700, color: C.teal }}>Spend</span>
          <span style={pillStyle(TONES.grey)}>Admins only</span>
        </div>
        {!loading && !error && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              height: '28px',
              padding: '0 11px',
              borderRadius: '7px',
              border: `1px solid ${s ? G.line : C.teal}`,
              background: s ? C.white : C.teal,
              color: s ? C.teal : 'white',
              fontSize: '0.66rem',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {s ? <Pencil size={11} /> : <Plus size={12} />}
            {s ? 'Change amount' : 'Add amount'}
          </button>
        )}
      </div>
      {children}
    </div>
  );

  let body: React.ReactNode;
  if (loading && !info) {
    body = <div style={{ fontSize: '0.7rem', color: C.muted }}>Loading…</div>;
  } else if (error) {
    body = <div style={{ fontSize: '0.7rem', color: '#c62828', fontWeight: 600 }}>{error}</div>;
  } else if (!s) {
    body = (
      <div style={{ fontSize: '0.7rem', color: C.sub, lineHeight: 1.55 }}>
        No amount added for this sync yet. Add what you spent to get these {info?.leads_count ?? 0} leads and see the cost per
        lead here and in the Spend Report.
      </div>
    );
  } else {
    const multi = (s.splits ?? []).length > 1;
    body = (
      <>
        <div style={{ display: 'flex', gap: '6.5px', flexWrap: 'wrap' }}>
          <StatTile label="Spent (with GST)" value={fmtINR(s.amount_incl_gst_paise)} />
          <StatTile label="Without GST" value={fmtINR(s.amount_excl_gst_paise)} color={C.tealMid} />
          <StatTile label="Leads" value={s.leads_count} />
          <StatTile label="Cost per lead" value={s.leads_count ? fmtINR(s.cost_per_lead_incl_gst) : '—'} color="#15803d" />
        </div>

        {multi && (
          <div style={{ border: `1px solid ${G.line}`, borderRadius: '8.3px', overflow: 'hidden' }}>
            {s.splits.map((x, i) => {
              const { incl } = inclExcl(x.amount_paise, s.gst_included);
              return (
                <div
                  key={x.campaign_key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '6.5px 10px',
                    borderTop: i ? `1px solid ${G.softer}` : 'none',
                    fontSize: '0.7rem',
                  }}
                >
                  <span style={{ flex: 1, minWidth: 0, fontWeight: 600, color: x.campaign_key === 'none' ? C.muted : '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {campaignLabel(x)}
                  </span>
                  <span style={{ color: C.muted, fontSize: '0.64rem', whiteSpace: 'nowrap' }}>{x.leads_count} leads</span>
                  <span style={{ width: '90px', textAlign: 'right', fontWeight: 700, color: C.teal }}>{fmtINR(incl)}</span>
                  <span style={{ width: '90px', textAlign: 'right', fontSize: '0.64rem', color: '#15803d', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {x.leads_count ? `${fmtINR(Math.round(incl / x.leads_count))}/lead` : '—'}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', fontSize: '0.62rem', color: C.muted }}>
          <span>
            Entered {s.gst_included ? 'with' : 'before'} GST · added by {userLabel(s.created_by)} on {fmtDateTime(s.created_at)}
            {s.updated_at && ` · last changed by ${userLabel(s.updated_by)} on ${fmtDateTime(s.updated_at)}`}
            {multi && ` · split ${s.split_mode === 'manual' ? 'by hand' : 'by lead count'}`}
          </span>
          {(s.history?.length ?? 0) > 0 && (
            <button
              type="button"
              onClick={() => setShowHistory((v) => !v)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', padding: 0, color: C.tealMid, fontSize: '0.64rem', fontWeight: 700, cursor: 'pointer' }}
            >
              <History size={11} />
              History ({s.history!.length})
              {showHistory ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
            </button>
          )}
        </div>

        {showHistory && <SpendHistory items={s.history ?? []} />}
      </>
    );
  }

  return (
    <>
      {wrap(body)}
      <SpendEditModal
        visible={editing}
        importId={importId}
        current={info}
        sheetName={sheetName}
        onHide={() => setEditing(false)}
        onSaved={(saved) => {
          setInfo((prev) => ({ data: saved, campaigns: saved.splits ?? prev?.campaigns ?? [], leads_count: saved.leads_count }));
          onChanged?.(saved);
        }}
      />
    </>
  );
};

// ── History (newest first) ───────────────────────────────────────────────────

const snapText = (x?: { amount_paise: number; gst_included: boolean } | null) =>
  x ? `${fmtINR(x.amount_paise)} ${x.gst_included ? 'incl. GST' : '+ GST'}` : '—';

const SpendHistory: React.FC<{ items: SpendChange[] }> = ({ items }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 0, borderLeft: `2px solid ${G.soft}`, marginLeft: '6px', paddingLeft: '12px' }}>
    {[...items].reverse().map((h, i) => (
      <div key={`${h.at}-${i}`} style={{ position: 'relative', padding: '5px 0' }}>
        <span
          style={{
            position: 'absolute',
            left: '-17.5px',
            top: '10px',
            width: 9,
            height: 9,
            borderRadius: '50%',
            background: h.action === 'set' ? '#16a34a' : C.lime,
            border: '2px solid white',
            boxShadow: `0 0 0 1px ${G.line}`,
          }}
        />
        <div style={{ fontSize: '0.7rem', color: '#374151', fontWeight: 600 }}>
          {h.action === 'set' ? (
            <>Added {snapText(h.after)}</>
          ) : (
            <>
              <span style={{ color: C.muted, textDecoration: 'line-through', fontWeight: 500 }}>{snapText(h.before)}</span> → {snapText(h.after)}
            </>
          )}
        </div>
        {h.reason && <div style={{ fontSize: '0.66rem', color: C.sub, marginTop: '1px' }}>“{h.reason}”</div>}
        <div style={{ fontSize: '0.6rem', color: C.muted, marginTop: '1px' }}>
          {userLabel(h.by)} · {fmtDateTime(h.at)}
        </div>
      </div>
    ))}
  </div>
);

export default SpendCard;
